<template>
  <div>
    <VDataTable
      :headers="headers"
      :items="items"
      item-key="title"
      hide-default-footer
      disable-pagination
      :no-data-text="noVacanciesText"
      :hide-default-header="vacancies.length === 0 || $vuetify.breakpoint.smAndDown"
      :mobile-breakpoint="0"
      class="vacancy-table"
    >
      <template v-slot:top>
        <VRow wrap>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="py-0"
          >
            <VTextField
              outlined
              dense
              :label="$t('search')"
              append-icon="mdi-magnify"
              hide-details
              autocomplete="off"
              @input="searching"
            />
          </VCol>
          <VCol
            cols="12"
            class="mb-5 mb-md-9 d-flex flex-column align-center align-sm-start align-md-center
          flex-md-row-reverse justify-md-end"
          >
            <VCard outlined tile class="mb-3 mb-md-0 ml-md-6">
              <VSheet
                color="yellow lighten-4"
                tile
                outlined
                class="caption pa-4 font-weight-medium"
                min-height="36"
              >
                {{ $t('recordsStoreTip') }}
              </VSheet>
            </VCard>
            <VacancyStoreButton />
          </VCol>
        </VRow>
      </template>

      <template v-slot:item="{ item }">
        <VacancyArchiveTableRow
          :key="item.id"
          :vacancy="item"
          :headers="headers"
          :limited-vacancies="limitedVacancies"
          @openDialogMaximumVacancies="openDialogMaximumVacancies"
          @openDialogExpiredTariff="openDialogExpiredTariff"
        />
      </template>
      <template v-slot:no-data>
        <div class="empty-row">
          {{ noVacanciesText }}
        </div>
      </template>
    </VDataTable>

    <VDialog
      v-model="dialogMaximumVacancies"
      persistent
      max-width="500"
    >
      <DialogLimitedVacancies
        :title="maxVacanciesTitle"
        :plan-title="planTitle"
        :limited-vacancies="limitedVacancies"
        @close="cancelDialogMaximumVacancies"
      />
    </VDialog>
    <VDialog
      v-model="dialogExpiredTariff"
      persistent
      max-width="600"
    >
      <DialogExpiredTariff @close="cancelDialogExpiredTariff" />
    </VDialog>
  </div>
</template>

<script>
import { prepareVacanciesForTable } from '@/helpers/vacancies';
import DialogLimitedVacanciesMixin from '@/mixins/DialogLimitedVacanciesMixin';
import DialogExpiredTariff from '@/components/DialogExpiredTariff.vue';
import VacancyStoreButton from './VacancyStoreButton.vue';
import VacancyArchiveTableRow from './VacancyArchiveTableRow.vue';

export default {
  name: 'VacancyArchiveTable',
  components: {
    VacancyStoreButton,
    VacancyArchiveTableRow,
    DialogExpiredTariff,
  },
  mixins: [DialogLimitedVacanciesMixin],
  props: {
    company: {
      type: Object,
      required: true,
    },
    vacancies: {
      type: Array,
      required: true,
    },
    noDataText: {
      type: String,
      default: null,
    },
    selectData: {
      type: Array,
      required: true,
    },
    selectedUsers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      searchText: '',
      dialogExpiredTariff: false,
      headers: [
        {
          text: this.$t('vacancy'),
          value: 'title',
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('creator'), value: 'creatorName', sortable: false,
        },
        { text: this.$t('city'), value: 'city', width: 130 },
        { text: this.$t('candidates'), value: 'candidatesCount' },
        { text: this.$t('interviews'), value: 'interviewCount' },
        {
          text: this.$t('testing'), value: 'hasTest', sortable: false,
        },
        { text: this.$t('archived'), value: 'archivedAt' },
        {
          text: '',
          value: 'action',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    items() {
      const items = prepareVacanciesForTable(this.vacancies);

      if (items.length > 0) {
        return items;
      }

      if (items.length === 0 && this.selectedUsers.length > 0) {
        this.$emit('clearSelectedUsers');
      }

      return items;
    },
    filteredVacancies() {
      if (this.searchText === null || this.searchText === '') {
        return this.vacancies;
      }

      return this.vacancies.filter((v) => v.title.toLowerCase().includes(this.searchText));
    },
    noVacanciesText() {
      if (this.searchText !== null && this.searchText !== '') {
        return this.$t('nothingFound');
      }

      return this.noDataText === null ? this.$t('noArchivedVacancies') : this.noDataText;
    },
  },
  watch: {
    async selectedUsers() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      this.timer = setTimeout(async () => {
        this.$emit('fetch', this.selectedUsers);
      }, 1500);
    },
  },
  methods: {
    searching(text) {
      this.searchText = text.toLowerCase();
    },
    openDialogExpiredTariff() {
      this.dialogExpiredTariff = true;
    },
    cancelDialogExpiredTariff() {
      this.dialogExpiredTariff = false;
    },
  },
};
</script>

<i18n locale="en" lang="yaml">
  search: Search
  recordsStoreTip: Interview records for archived jobs are stored for 180 days.
  vacancy: Job
  city: City
  candidates: Candidates
  interviews: Interviews
  archived: Archived at
  noArchivedVacancies: No archived jobs
  nothingFound: Nothing found
  creator: Creator
  testing: Testing
  maxVacanciesCopyTitle: "You cannot copy the job"
  maxVacanciesRestoreTitle: 'You cannot restore the job'
</i18n>

<i18n locale="ru" lang="yaml">
  search: Поиск
  recordsStoreTip: Записи интервью для архивных вакансий хранятся в течение 180 дней.
  vacancy: Вакансия
  city: Город
  candidates: Кандидаты
  interviews: Интервью
  archived: Перенесена в архив
  noArchivedVacancies: В архиве нет вакансий
  nothingFound: Ничего не найдено
  creator: Создатель
  testing: Тестирование
  maxVacanciesCopyTitle: 'Вы не можете скопировать вакансию'
  maxVacanciesRestoreTitle: 'Вы не можете восстановить вакансию'
</i18n>
